import { useEffect, useMemo, useState } from 'react';
import { OFF } from '../../lib/off';
import { Link, useNavigate } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type AvailableNumber, type CarrierStatus, type NumberOrder } from '../../api/client';
import { AddressChooser, OrderLines, ORDER_STATUS, choiceOf, draftAddress, draftErrors, draftFor, isTollFree, oneLine, STATES, type AddressDraft } from '../../components/numbers';
import { NewNumbersGoTo } from '../../components/NewNumbersGoTo';
import { useTenant } from '../../components/Shell';
import { AskStitch } from '../../components/AskStitch';
import { Button, Card, Chip, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const STEPS = [
  { id: 'find', title: 'Find numbers' },
  { id: 'calls', title: 'Where calls go' },
  { id: 'e911', title: '911 address' },
  { id: 'review', title: 'Review' }
] as const;
type StepId = (typeof STEPS)[number]['id'];

/** Why a company can't get numbers here right now, in their words. */
export function orderingBlocked(s: CarrierStatus | undefined, staff: boolean): string | null {
  if (!s) return null;
  if (!s.carrier) return OFF.numbersNoCarrier;
  if (!s.writes) return OFF.numbersSpendingOff;
  if (!s.routed) return OFF.numbersNoCallFlow;
  if (staff) return null;
  if (!s.policy.canOrder) return OFF.numbersCompanyOff;
  if (s.remaining <= 0) return `Your company has had its ${s.policy.monthlyLimit} new numbers for this month. Ask Stitch support if you need more.`;
  return null;
}

const money = (n: number | undefined) => (n === undefined ? '' : `$${n.toFixed(2)}`);

/** Getting new numbers: find them, choose where calls go and their 911 address, then confirm. */
export function GetNumbers() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const navigate = useNavigate();
  const { state } = useAuth();
  const staff = Boolean(state?.user?.staff);
  const status = useData(() => api.carrierStatus(d), [d]);
  const e911 = useData(() => api.e911(d).catch(() => undefined), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);

  const [step, setStep] = useState<StepId>('find');
  const [tollFree, setTollFree] = useState(false);
  const [usState, setUsState] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [rateCenter, setRateCenter] = useState('');
  const [found, setFound] = useState<AvailableNumber[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [picked, setPicked] = useState<AvailableNumber[]>([]);
  const [destination, setDestination] = useState('');
  const [waiting, setWaiting] = useState(false);
  const inInventory = waiting && Boolean(status.data?.inventory);
  const [address, setAddress] = useState<AddressDraft | null>(null);
  const [agree, setAgree] = useState(false);
  const [agreeAddress, setAgreeAddress] = useState(false);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState<NumberOrder | null>(null);

  const saved = e911.data?.addresses ?? [];
  // Start in the state of their usual address, with it chosen for 911.
  useEffect(() => {
    if (!e911.data || address) return;
    setAddress(draftFor(e911.data.addresses));
    if (!usState && e911.data.addresses[0]) setUsState(e911.data.addresses[0].state);
  }, [e911.data, address, usState]);
  const centers = useData(() => (usState && !tollFree ? api.rateCenters(d, usState).then((r) => r.rateCenters) : Promise.resolve([])), [d, usState, tollFree]);

  const max = Math.min(20, status.data?.remaining ?? 0);
  const blocked = orderingBlocked(status.data, staff);
  const allTollFree = picked.length > 0 && picked.every((n) => isTollFree(n.number));
  const index = STEPS.findIndex((s) => s.id === step);
  const draft = address ?? { mode: 'none' as const };
  const chosen = draftAddress(draft, saved);
  const needsAddress = !allTollFree && draft.mode !== 'none';

  async function search() {
    setSearchError('');
    if (!tollFree && !usState) {
      setSearchError('Choose a state.');
      return;
    }
    if (areaCode && !/^[2-9]\d{2}$/.test(areaCode)) {
      setSearchError('An area code is three digits, like 410.');
      return;
    }
    setSearching(true);
    try {
      const r = await api.searchNumbers(d, tollFree ? { tollFree: true } : { state: usState, ...(areaCode ? { areaCode } : {}), ...(rateCenter ? { rateCenter } : {}) });
      setFound(r.numbers);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : String(e));
      setFound(null);
    } finally {
      setSearching(false);
    }
  }

  const toggle = (n: AvailableNumber) =>
    setPicked((p) => (p.some((x) => x.number === n.number) ? p.filter((x) => x.number !== n.number) : p.length >= max ? p : [...p, n]));

  /** What's missing on a step, or '' when it's done. */
  function problem(id: StepId): string {
    if (id === 'find') return picked.length ? '' : 'Choose at least one number.';
    if (id === 'calls') return destination || inInventory ? '' : 'Choose where calls to the new numbers go.';
    if (id === 'e911') return allTollFree || Object.keys(draftErrors(draft)).length === 0 ? '' : 'Check the 911 address.';
    if (!agree) return 'Confirm the numbers are added to your company’s bill.';
    if (needsAddress && !agreeAddress) return 'Confirm the 911 address is where people using the numbers are.';
    return '';
  }

  function goTo(id: StepId) {
    const target = STEPS.findIndex((s) => s.id === id);
    // Forward only past steps that are done.
    for (let i = 0; i < target; i++) {
      if (problem(STEPS[i]!.id)) {
        setTried(true);
        setStep(STEPS[i]!.id);
        return;
      }
    }
    setTried(false);
    setStep(id);
  }

  async function submit() {
    setTried(true);
    // What's missing shows next to it, on the first step that needs something.
    const bad = STEPS.find((x) => problem(x.id));
    if (bad) {
      setError('');
      setStep(bad.id);
      return;
    }
    setSending(true);
    setError('');
    try {
      const address = allTollFree ? null : choiceOf(draft);
      const r = await api.orderNumbers(d, { numbers: picked.map((n) => n.number), destination: inInventory ? '' : destination, address, confirm: true, ...(address ? { confirmAddress: agreeAddress } : {}) });
      setOrder(r.order);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  if (order) return <OrderProgress d={d} initial={order} />;

  const header = <PageHeader eyebrow={<Link to={`${base}/numbers`}>Numbers</Link>} title="Get new numbers" />;
  if (status.error)
    return (
      <>
        {header}
        <div className="content">
          <Card>
            <LoadError error={status.error} retry={status.reload} />
          </Card>
        </div>
      </>
    );
  if (!status.data || (!e911.data && e911.loading))
    return (
      <>
        {header}
        <Loading label="Loading" />
      </>
    );
  if (blocked)
    return (
      <>
        {header}
        <div className="content">
          <Notice title="You can’t get numbers here right now" icon="lock">
            {blocked}
          </Notice>
          {!staff && <AskStitch tenant={d} feature="numbers-buy" />}
          <div>
            <Link className="btn secondary" to={`${base}/numbers`}>
              Back to Numbers
            </Link>
          </div>
        </div>
      </>
    );

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`${base}/numbers`}>Numbers</Link>
            {staff ? ' · Stitch staff: no monthly limit' : ` · ${plural(status.data.remaining, 'more number')} this month`}
          </>
        }
        title="Get new numbers"
      />
      <div className="content">
        {!tenant.writable && <Notice icon="lock">{d} is read-only in this environment, so numbers can be found here but not bought.</Notice>}
        <div className="wizard-layout">
          <nav className="wizard-steps" aria-label="Steps">
            <ol>
              {STEPS.map((s, i) => (
                <li key={s.id} className={s.id === step ? 'current' : i < index ? 'past' : undefined}>
                  <button type="button" onClick={() => goTo(s.id)} aria-current={s.id === step ? 'step' : undefined}>
                    <span className="wizard-num" aria-hidden="true">
                      {i < index ? <Icon name="check" size={13} strokeWidth={2.6} /> : i + 1}
                    </span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <Card title={STEPS[index]!.title}>
            <form
              className="card-body wizard-body"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                const next = STEPS[index + 1];
                if (next) goTo(next.id);
                else void submit();
              }}
            >
              {step === 'find' && (
                <>
                  <div className="segmented" role="radiogroup" aria-label="Kind of number">
                    <label className={!tollFree ? 'on' : ''}>
                      <input type="radio" name="kind" checked={!tollFree} onChange={() => (setTollFree(false), setFound(null))} />
                      Local
                    </label>
                    <label className={tollFree ? 'on' : ''}>
                      <input type="radio" name="kind" checked={tollFree} onChange={() => (setTollFree(true), setFound(null))} />
                      Toll-free
                    </label>
                  </div>
                  {!tollFree ? (
                    <div className="number-search">
                      <div className="field">
                        <label htmlFor="gn-state">State</label>
                        <select id="gn-state" className="input" value={usState} onChange={(e) => (setUsState(e.target.value), setRateCenter(''))}>
                          <option value="">Choose</option>
                          {STATES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field">
                        <label htmlFor="gn-city">City or area</label>
                        <select id="gn-city" className="input" value={rateCenter} onChange={(e) => setRateCenter(e.target.value)} disabled={!usState || !centers.data?.length}>
                          <option value="">Anywhere in the state</option>
                          {(centers.data ?? []).map((c) => (
                            <option key={c.rateCenter} value={c.rateCenter}>
                              {c.city || c.rateCenter}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Field label="Area code" inputMode="numeric" maxLength={3} value={areaCode} onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ''))} hint="Optional." />
                    </div>
                  ) : (
                    <p className="wizard-lede">Toll-free numbers (800, 833, 844, 855, 866, 877, 888) are free for callers anywhere in the US.</p>
                  )}
                  <div>
                    <Button variant={found ? 'secondary' : 'primary'} icon="search" onClick={() => void search()} disabled={searching}>
                      {searching ? 'Searching…' : found ? 'Search again' : 'Search'}
                    </Button>
                  </div>
                  {searchError && <span className="error">{searchError}</span>}
                  {found &&
                    (found.length === 0 ? (
                      <Empty title="None free there right now">Try another city, area code or state.</Empty>
                    ) : (
                      <fieldset className="plain-fieldset">
                        <legend className="field-label">
                          Choose up to {plural(max, 'number')} {picked.length > 0 && <span className="muted">· {picked.length} chosen</span>}
                        </legend>
                        <div className="number-results">
                          {found.map((n) => {
                            const on = picked.some((x) => x.number === n.number);
                            return (
                              <label key={n.number} className={`number-pick${on ? ' on' : ''}${!on && picked.length >= max ? ' disabled' : ''}`}>
                                <input type="checkbox" checked={on} disabled={!on && picked.length >= max} onChange={() => toggle(n)} />
                                <span className="cell-stack">
                                  <strong className="mono">{formatPhoneNumber(n.number)}</strong>
                                  <small>{n.tollFree ? 'Toll-free' : [n.city, n.state].filter(Boolean).join(', ')}</small>
                                  {staff && n.price && (
                                    <small className="staff-price">
                                      {money(n.price.monthly)}/mo{n.price.setup ? ` · ${money(n.price.setup)} setup` : ''}
                                    </small>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                    ))}
                  {picked.length > 0 && (
                    <div className="picked">
                      <span className="field-label">Chosen</span>
                      <ul className="picked-list">
                        {picked.map((n) => (
                          <li key={n.number}>
                            <span className="mono">{formatPhoneNumber(n.number)}</span>
                            <button type="button" className="icon-btn" aria-label={`Remove ${formatPhoneNumber(n.number)}`} onClick={() => toggle(n)}>
                              <Icon name="x" size={14} strokeWidth={2.2} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {tried && problem('find') && <span className="error">{problem('find')}</span>}
                </>
              )}

              {step === 'calls' && (
                <>
                  <p className="wizard-lede">Calls to {picked.length === 1 ? 'the new number' : `all ${picked.length} new numbers`} go here. You can change each one later on Numbers.</p>
                  <NewNumbersGoTo
                    inventory={Boolean(status.data?.inventory)}
                    waiting={inInventory}
                    onWaiting={setWaiting}
                    destination={destination}
                    onDestination={setDestination}
                    dir={dir.data}
                    company={tenant.name}
                    many={picked.length !== 1}
                    error={tried ? problem('calls') || undefined : undefined}
                  />
                </>
              )}

              {step === 'e911' && (
                <>
                  {allTollFree ? (
                    <Notice>Toll-free numbers don’t have a 911 address: when someone calls 911, the local number they call out with is used. Nothing to set here.</Notice>
                  ) : (
                    <>
                      <p className="wizard-lede">
                        When someone calls 911 from {picked.length === 1 ? 'this number' : 'these numbers'}, this is where emergency services are sent. Use where people with the number work, including the floor or suite.
                      </p>
                      <AddressChooser
                        saved={saved}
                        value={draft}
                        onChange={setAddress}
                        showErrors={tried}
                        none="Calls to 911 from these numbers won’t send an address until you set one on Emergency addresses."
                      />
                      {picked.some((n) => isTollFree(n.number)) && <span className="hint">Toll-free numbers you chose don’t get one.</span>}
                    </>
                  )}
                </>
              )}

              {step === 'review' && (
                <>
                  <dl className="dl">
                    <dt>{plural(picked.length, 'number')}</dt>
                    <dd>
                      <ul className="plain-list">
                        {picked.map((n) => (
                          <li key={n.number}>
                            <span className="mono">{formatPhoneNumber(n.number)}</span>
                            <span className="muted">{n.tollFree ? 'Toll-free' : [n.city, n.state].filter(Boolean).join(', ')}</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                    <dt>Calls go to</dt>
                    <dd>{inInventory ? 'Open inventory, for now' : destination ? lookup(dir.data, destination).name : '—'}</dd>
                    <dt>911 address</dt>
                    <dd>{allTollFree ? 'Not needed (toll-free)' : chosen ? <>{chosen.label && <strong>{chosen.label}: </strong>}{oneLine(chosen)}</> : <Chip small tone="warn">Not set yet</Chip>}</dd>
                  </dl>
                  <label className="check confirm-check">
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                    <span>
                      Add {picked.length === 1 ? 'this number' : `these ${picked.length} numbers`} to {tenant.name}’s bill with Stitch.
                      <span className="hint">
                        {' '}
                        Billed monthly from today, as in your agreement with Stitch. New numbers stay on your bill for at least {status.data.minHoldDays ?? 90} days, even if you stop using them sooner.
                      </span>
                    </span>
                  </label>
                  {needsAddress && chosen && (
                    <label className="check confirm-check">
                      <input type="checkbox" checked={agreeAddress} onChange={(e) => setAgreeAddress(e.target.checked)} />
                      <span>
                        People who call out with {picked.length === 1 ? 'this number' : 'these numbers'} are at <strong>{oneLine(chosen)}</strong>.
                        <span className="hint"> It’s where emergency services are sent when they call 911.</span>
                      </span>
                    </label>
                  )}
                  {tried && problem('review') && <span className="error">{problem('review')}</span>}
                  {!staff && <p className="muted small-print">After this, your company can get {plural(Math.max(0, (status.data.remaining ?? 0) - picked.length), 'more number')} this month.</p>}
                  {error && <Notice tone="crit">{error}</Notice>}
                </>
              )}

              <div className="wizard-actions">
                {index > 0 && (
                  <Button variant="ghost" onClick={() => goTo(STEPS[index - 1]!.id)}>
                    Back
                  </Button>
                )}
                {step !== 'review' ? (
                  <Button variant="primary" type="submit">
                    Next: {STEPS[index + 1]!.title}
                  </Button>
                ) : (
                  <Button variant="primary" type="submit" icon="cart" disabled={sending || !tenant.writable}>
                    {sending ? 'Getting them…' : `Get ${plural(picked.length, 'number')}`}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate(`${base}/numbers`)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
}

/** An order coming through: each number as it's bought, gets its 911 address and starts taking calls. */
export function OrderProgress({ d, initial }: { d: string; initial: NumberOrder }) {
  const base = `/t/${encodeURIComponent(d)}`;
  const [order, setOrder] = useState(initial);
  useEffect(() => {
    if (order.status !== 'working') return;
    const t = window.setTimeout(() => {
      api
        .numberOrder(d, order.id)
        .then((r) => setOrder(r.order))
        .catch(() => undefined);
    }, 1500);
    return () => window.clearTimeout(t);
  }, [d, order]);
  const s = ORDER_STATUS[order.status];
  const ready = order.lines.filter((l) => l.pbx === 'added').length;
  const gone = order.lines.filter((l) => !l.bought).length;
  const stuck = order.lines.filter((l) => l.bought && l.pbx !== 'added').length;
  const title = order.status === 'working' ? 'Getting your numbers' : order.status === 'done' ? (order.lines.length === 1 ? 'Your new number is ready' : 'Your new numbers are ready') : order.status === 'failed' ? 'The numbers didn’t go through' : 'Your numbers are nearly ready';
  return (
    <>
      <PageHeader eyebrow={<Link to={`${base}/numbers`}>Numbers</Link>} title={title} chips={<Chip tone={s.tone}>{s.label}</Chip>} />
      <div className="content">
        <Card>
          <div className="card-body section-stack">
            {order.status === 'working' && (
              <p className="wizard-lede" role="status">
                <span className="spinner inline" aria-hidden="true" /> Buying them, setting their 911 address and connecting them. This takes a few seconds.
              </p>
            )}
            {order.status === 'done' && <p className="wizard-lede">Calls to {order.lines.length === 1 ? 'it' : 'them'} are already coming through. Change where each one goes any time on Numbers.</p>}
            {order.status === 'needs_staff' && (
              <p className="wizard-lede">They’re yours, with their 911 address. Stitch support is connecting them to your phone system and will let you know when they take calls, usually the same working day.</p>
            )}
            {order.status === 'partial' && (
              <p className="wizard-lede">
                {plural(ready, 'number')} {ready === 1 ? 'is' : 'are'} ready.
                {gone > 0 && ` ${gone === 1 ? 'One was' : `${gone} were`} taken by someone else just as you chose ${gone === 1 ? 'it' : 'them'}, so ${gone === 1 ? 'it isn’t' : 'they aren’t'} on your bill: choose ${gone === 1 ? 'another' : 'others'} with Get numbers.`}
                {stuck > 0 && ` Stitch support is finishing ${stuck === 1 ? 'the other one' : `the other ${stuck}`}; you’ll see ${stuck === 1 ? 'it' : 'them'} on Numbers once ${stuck === 1 ? 'it’s' : 'they’re'} ready.`}
              </p>
            )}
            {order.status === 'failed' && <p className="wizard-lede">Nothing was added to your bill. {order.lines[0]?.error ?? order.error ?? ''} Try other numbers, or ask Stitch support.</p>}
            <OrderLines order={order} />
            <div className="wizard-actions">
              <Link className="btn primary" to={`${base}/numbers`}>
                Back to Numbers
              </Link>
              {order.status !== 'working' && (
                <Link className="btn secondary" to={`${base}/emergency`}>
                  Emergency addresses
                </Link>
              )}
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
